/**
 * Error types for the api layer — docs/spec/06 R2.
 *
 * Stage 1 adds the status → user-message mapping table on top of these. What
 * lives here is the *shape*: enough structure that the retry policy (§4) and
 * the mapping can both make decisions without re-parsing a response.
 *
 * The rule these types exist to enforce: an error must never reach a user as a
 * blanket string. "Internal Server Error" is exactly what happens when a client
 * throws away the status and the envelope, so we keep both.
 */

/** The api's JSON error envelope (02 §6). Field errors drive inline form errors. */
export interface ApiErrorBody {
  readonly message?: string
  readonly requestId?: string
  /** Per-field validation messages, for a 400 (06 R2 → inline, never a modal). */
  readonly fieldErrors?: Readonly<Record<string, readonly string[]>>
}

/** A response arrived and it was not ok. `status` is always meaningful. */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody
  /** `Retry-After` in seconds, when the api sent one (honoured for 429 — 06 §4). */
  readonly retryAfterSeconds: number | null

  constructor(status: number, body: ApiErrorBody = {}, retryAfterSeconds: number | null = null) {
    super(body.message ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.retryAfterSeconds = retryAfterSeconds
  }

  get requestId(): string | null {
    return this.body.requestId ?? null
  }

  get fieldErrors(): Readonly<Record<string, readonly string[]>> | null {
    return this.body.fieldErrors ?? null
  }
}

/**
 * The request never got an answer — offline, DNS, timeout, connection reset.
 *
 * Distinguished from ApiError because it is the ONE case where retrying a
 * mutation is safe: nothing reached the server, so nothing can be duplicated
 * (06 §4). Everything else must not auto-retry — duplicate side-effects are
 * precisely what R3 exists to prevent.
 */
export class NetworkError extends Error {
  constructor(message = "Can't reach the server. Check your connection.", options?: ErrorOptions) {
    super(message, options)
    this.name = 'NetworkError'
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError
}
